import React, { useState } from 'react';
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MuiButton from 'app/design/uiComponents/MuiButton';
import { Condition, Strategy } from './types';
import { ConditionItem } from './ConditionItem';
import { ConditionText } from './ConditionText';
import { ConditionOperataor } from './ConditionOperataor';
import { FieldData } from './fields/types';
import {
  addFieldInStrategy,
  editFieldInStrategy,
  addNewConditionInStrategy,
  getConditionsForBackendFromStrategy,
  getStrategyFromConditions,
  removeConditionInStrategy,
  removeFieldInStrategy,
  pasteConditionInStrategy,
  getConditionsForFrontEndFromStrategy,
  StrategyMode,
} from './utils/strategy-data';

import { cloneDeep } from 'lodash';
import { checkConditions, getNextKeys } from './utils/StrategyValidation';
import { useIsFeatureFlag } from 'app/components/Common/FeatureFlag';
import { FEATURES } from 'types/Feature';
import { FeatureFlag } from 'app/components/Common/FeatureFlag';

interface Props {
  inputConditions?: FieldData[];
  copiedStrategy?: Condition;
  onSave?: (conditionsForBackend: FieldData[]) => void;
  setStrategy?: (conditionsForBackend: FieldData[]) => void;
  updateCopiedStrategy?: (copiedStrategy: Condition) => void;
  mode: string;
  isValid: boolean;
  defaultResolution?: string;
  blockType?: 'entry' | 'exit'; // naya prop add kiya
  onChartinkRemoved?: () => void; // <-- add this prop
  positionDirection?: string; // <-- add this prop
  onRemoveChartinkBothExit?: (exitType: 'long' | 'short') => void; // <-- new prop
  exitType?: 'long' | 'short'; // <-- new prop for exit block
  onRemoveChartinkBothEntry?: (entryType: 'long' | 'short') => void; // <-- new prop
  entryType?: 'long' | 'short'; // <-- new prop for entry block
}

const getEmptyStrategy = (): Strategy => ({
  condition_key: '',
  conditions: [
    {
      name: 'Long Entry Condition 1',
      id: '76d8b9d9-acb9-4e40-9758-cdc36373f62c',
      type: 'condition',
      fields: [],
    },
  ],
});

export default function StrategyBuilder(props: Props) {
  const {
    onSave,
    setStrategy,
    copiedStrategy,
    updateCopiedStrategy,
    mode,
    defaultResolution,
    inputConditions,
    blockType,
    positionDirection,
    onRemoveChartinkBothExit,
    exitType,
    onRemoveChartinkBothEntry,
    entryType,
  } = props;

  // Yahan par hum blockType prop use kar rahe hain, taki entry/exit sahi set ho
  const inputStrategy: Strategy =
    inputConditions && inputConditions.length > 0
      ? getStrategyFromConditions(inputConditions, props.blockType || 'entry')
      : getEmptyStrategy();

  inputStrategy.conditions = getConditionsForFrontEndFromStrategy(
    inputStrategy || {},
  );
  const [outputStrategy, setOutputStrategy] = useState<Strategy>(
    inputStrategy || {},
  );
  const { conditions } = outputStrategy;
  const isChartinkActive =
    conditions.length === 1 &&
    conditions[0].fields.length === 1 &&
    conditions[0].fields[0].key === 'chartink';

  const updateOutputStrategy = React.useCallback(
    (strategy: Strategy) => {
      setOutputStrategy({ ...strategy });
      if (setStrategy) {
        setStrategy(getConditionsForBackendFromStrategy(strategy));
      }
    },
    [setOutputStrategy, setStrategy],
  );

  // Handler to add Chartink condition to a block (append, don't clear others)
  const handleAddChartink = React.useCallback(
    (conditionIndex: number) => {
      const newConditions = [...conditions];
      const chartinkField = {
        key: 'chartink',
        type: 'external_signal',
        params: { label: 'chartink' },
      };
      // Only add if not already present
      if (
        !newConditions[conditionIndex].fields.some(f => f.key === 'chartink')
      ) {
        newConditions[conditionIndex] = {
          ...newConditions[conditionIndex],
          fields: [...newConditions[conditionIndex].fields, chartinkField],
        };
        updateOutputStrategy({ ...outputStrategy, conditions: newConditions });
      }
    },
    [conditions, outputStrategy, updateOutputStrategy],
  );

  // Dialog state for Chartink remove confirmation
  const [openChartinkDialog, setOpenChartinkDialog] = React.useState(false);
  // Track which exitType ('long' or 'short') or entryType is being removed
  const [pendingExitType, setPendingExitType] = React.useState<
    'long' | 'short' | null
  >(null);
  const [pendingEntryType, setPendingEntryType] = React.useState<
    'long' | 'short' | null
  >(null);
  const [pendingBlockType, setPendingBlockType] = React.useState<
    'entry' | 'exit' | null
  >(null);
  const [pendingRemoveIndex, setPendingRemoveIndex] = React.useState<
    number | null
  >(null);

  // Handler to remove Chartink condition from a block
  const handleRemoveChartink = React.useCallback(
    (conditionIndex: number) => {
      if (blockType === 'exit' && positionDirection === 'both' && exitType) {
        setPendingRemoveIndex(conditionIndex);
        setPendingExitType(exitType);
        setPendingBlockType('exit');
        setOpenChartinkDialog(true);
      } else if (
        blockType === 'entry' &&
        positionDirection === 'both' &&
        entryType
      ) {
        setPendingRemoveIndex(conditionIndex);
        setPendingEntryType(entryType);
        setPendingBlockType('entry');
        setOpenChartinkDialog(true);
      } else {
        // Remove Chartink from this block only (entry or single exit)
        const newConditions = [...conditions];
        newConditions[conditionIndex] = {
          ...newConditions[conditionIndex],
          fields: newConditions[conditionIndex].fields.filter(
            f => f.key !== 'chartink',
          ),
        };
        updateOutputStrategy({ ...outputStrategy, conditions: newConditions });
        if (props.onChartinkRemoved) props.onChartinkRemoved();
      }
    },
    [
      blockType,
      positionDirection,
      exitType,
      entryType,
      conditions,
      outputStrategy,
      updateOutputStrategy,
      props,
    ],
  );

  // Confirm dialog OK handler
  const handleConfirmRemoveChartink = () => {
    setOpenChartinkDialog(false);
    setPendingRemoveIndex(null);
    if (
      pendingBlockType === 'exit' &&
      onRemoveChartinkBothExit &&
      pendingExitType
    ) {
      onRemoveChartinkBothExit(pendingExitType);
    }
    if (
      pendingBlockType === 'entry' &&
      onRemoveChartinkBothEntry &&
      pendingEntryType
    ) {
      onRemoveChartinkBothEntry(pendingEntryType);
    }
    setPendingExitType(null);
    setPendingEntryType(null);
    setPendingBlockType(null);
  };
  // Cancel dialog handler
  const handleCancelRemoveChartink = () => {
    setOpenChartinkDialog(false);
    setPendingRemoveIndex(null);
    setPendingExitType(null);
    setPendingEntryType(null);
    setPendingBlockType(null);
  };
  const isHistoryIndicator = useIsFeatureFlag([FEATURES.IS_HISTORY_INDICATOR]);
  const isChartinkEnabled = useIsFeatureFlag([FEATURES.IS_CHARTINK_ENABLED]);

  React.useEffect(() => {
    setOutputStrategy({ ...inputStrategy });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputConditions]);

  const onAddField = React.useCallback(
    (conditionIndex: number, field: FieldData, fieldIndex: number) => {
      if (field && field.type == 'indicators' && isHistoryIndicator) {
        field = {
          ...field,
          type: 'historyindicators',
        };
      }
      updateOutputStrategy(
        addFieldInStrategy(outputStrategy, conditionIndex, field, fieldIndex),
      );
    },
    [outputStrategy, updateOutputStrategy, isHistoryIndicator],
  );
  // const onAddFieldAtPosition = React.useCallback(
  //   (conditionIndex: number, field: FieldData, fieldIndex: number) => {
  //     updateOutputStrategy(
  //       addFieldAtPositionInStrategy(
  //         outputStrategy,
  //         conditionIndex,
  //         field,
  //         fieldIndex,
  //       ),
  //     );
  //   },
  //   [outputStrategy, updateOutputStrategy],
  // );
  const onRemoveField = React.useCallback(
    (conditionIndex: number, fieldIndex: number) => {
      updateOutputStrategy(
        removeFieldInStrategy(outputStrategy, conditionIndex, fieldIndex),
      );
    },
    [outputStrategy, updateOutputStrategy],
  );
  const onEditField = React.useCallback(
    (conditionIndex: number, field: FieldData, fieldIndex: number) => {
      updateOutputStrategy(
        editFieldInStrategy(outputStrategy, conditionIndex, field, fieldIndex),
      );
    },
    [outputStrategy, updateOutputStrategy],
  );

  const onConditionOperatorChange = React.useCallback(
    (conditionIndex: number, key: string) => {
      const { conditions } = outputStrategy;
      const condition = conditions[conditionIndex];
      const newFields = [
        {
          key,
          type: 'condition',
          params: {
            type: 'outer',
          },
        },
      ];
      condition.fields = newFields;
      updateOutputStrategy(outputStrategy);
    },
    [outputStrategy, updateOutputStrategy],
  );

  const onAddCondition = () => {
    updateOutputStrategy(
      addNewConditionInStrategy(
        outputStrategy,
        outputStrategy.conditions.length,
      ),
    );
  };

  const onRemoveCondition = React.useCallback(
    (conditionIndex: number) => {
      updateOutputStrategy(
        removeConditionInStrategy(outputStrategy, conditionIndex),
      );
    },
    [outputStrategy, updateOutputStrategy],
  );

  const onCopyCondition = (conditionIndex: number) => {
    if (updateCopiedStrategy) {
      updateCopiedStrategy(
        cloneDeep(outputStrategy.conditions[conditionIndex]),
      );
    }
  };

  const onPasteCondition = React.useCallback(
    (conditionIndex: number) => {
      if (copiedStrategy) {
        updateOutputStrategy(
          pasteConditionInStrategy(
            outputStrategy,
            conditionIndex,
            copiedStrategy,
          ),
        );
      }
    },
    [copiedStrategy, outputStrategy, updateOutputStrategy],
  );

  const onSaveButtonClick = React.useCallback(() => {
    const conditionsForBackend =
      getConditionsForBackendFromStrategy(outputStrategy);
    if (onSave) {
      onSave(conditionsForBackend);
    }
  }, [outputStrategy, onSave]);
  let conditionCounter = 1;

  const getConditionKey = (condition: Condition) => {
    if (condition && condition.fields && condition.fields[0]) {
      return condition.fields[0].key;
    }
    return '';
  };

  const isOrPresent = conditions.some((condition: Condition) => {
    return getConditionKey(condition) === 'or';
  });

  const getPossibleValues = (index: number, fields: FieldData[]) => {
    const preConditions = fields.slice(0, index);
    const matchResult = checkConditions(preConditions);
    return getNextKeys(matchResult);
  };

  const parentConditionKey = outputStrategy.condition_key;

  // Make isChartinkActive block-specific
  const isChartinkActiveForBlock = (fields: FieldData[]) =>
    fields.length === 1 && fields[0].key === 'chartink';

  return (
    <div>
      {/* Chartink Remove Confirmation Dialog */}
      <FeatureFlag features={[FEATURES.IS_CHARTINK_ENABLED]} fallback={<></>}>
        <Dialog open={openChartinkDialog} onClose={handleCancelRemoveChartink}>
          <DialogTitle>Remove Chartink?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {pendingBlockType === 'exit'
                ? 'Removing Chartink will delete it from both Long and Short exit conditions. Are you sure you want to continue?'
                : 'Removing Chartink will delete it from both Long and Short entry conditions. Are you sure you want to continue?'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelRemoveChartink} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmRemoveChartink}
              color="primary"
              autoFocus
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </FeatureFlag>
      {conditions.map((condition, index) => {
        // Print the entire condition object for debugging
        if (condition.type === 'condition') {
          // Determine block type using blockType property
          const isExit = condition.blockType === 'exit';
          const isEntry = condition.blockType === 'entry';
          const hasChartink = condition.fields.some(f => f.key === 'chartink');
          // Debug logs for block analysis
         
          if (mode === StrategyMode.TEXT) {
            return (
              <Box
                marginLeft={
                  isOrPresent &&
                  (getConditionKey(conditions[index + 1]) === 'and' ||
                    getConditionKey(conditions[index - 1]) === 'and')
                    ? '60px'
                    : '0px'
                }
                key={condition.id}
              >
                <ConditionText
                  key={condition.id}
                  condition={condition}
                  index={conditionCounter++}
                />
              </Box>
            );
          } else {
            return (
              <Box
                key={condition.id}
                marginLeft={
                  isOrPresent &&
                  (getConditionKey(conditions[index + 1]) === 'and' ||
                    getConditionKey(conditions[index - 1]) === 'and')
                    ? '60px'
                    : '0px'
                }
              >
                <ConditionItem
                  condition={condition}
                  conditionIndex={index}
                  onAddField={onAddField}
                  // onAddFieldAtPosition={onAddFieldAtPosition}
                  onRemoveField={onRemoveField}
                  onEditField={onEditField}
                  onRemoveCondition={onRemoveCondition}
                  onCopyCondition={onCopyCondition}
                  onPasteCondition={onPasteCondition}
                  getPossibleValues={getPossibleValues}
                  copiedStrategy={copiedStrategy}
                  mode={mode}
                  index={conditionCounter++}
                  defaultResolution={defaultResolution}
                  isChartinkActive={isChartinkEnabled ? hasChartink : false}
                  allowNormalWithChartink={false}
                  onRemoveChartink={
                    isChartinkEnabled
                      ? () => handleRemoveChartink(index)
                      : undefined
                  }
                  // Add Chartink handlers if needed
                />
                {mode === StrategyMode.EDIT &&
                  (isExit || (isEntry && !hasChartink)) && (
                    <MuiButton
                      variant="contained"
                      color="secondary"
                      onClick={onAddCondition}
                      startIcon={<AddIcon />}
                      sx={{ mt: 2 }}
                    >
                      Add
                    </MuiButton>
                  )}
              </Box>
            );
          }
        } else {
          return (
            <Box
              marginLeft={
                isOrPresent && getConditionKey(conditions[index]) === 'and'
                  ? '60px'
                  : '0px'
              }
              key={condition.id}
            >
              <ConditionOperataor
                key={condition.id}
                condition={condition}
                conditionIndex={index}
                onConditionOperatorChange={onConditionOperatorChange}
                mode={mode}
              />
            </Box>
          );
        }
      })}
      {/*
        Global Add button ab nahi dikhana hai, kyunki per-block Add button use ho raha hai
      */}
      {onSave && <Button onClick={onSaveButtonClick}>Save</Button>}
    </div>
  );
}
